import { Body, Heading, IconButton } from '@assembly-js/design-system';
import clsx from 'clsx';

import { useFileIcon } from 'src/hooks/useFileIcon';
import { getHumanReadableFileSize } from 'src/utils/FileHelpers';

export type FileInputMetaData = {
  name: string;
  size: number;
  extension: string;
  readOnly: boolean;
  fileKey?: string;
};

interface FileInputItemProps extends FileInputMetaData {
  className?: string;
  containerClassName?: string;
  onClick?: () => void;
  onRemove: () => void;
  onDownload?: () => void;
}

/**
 * This component is responsible for rendering the dropped/input files.
 * It renders the file name, file size and icons (file meta data)
 * @param name: string - file name
 * @param size: number - file size
 * @param extension: string - file extension: used to get the file icon to be rendered.
 * @returns {JSX.Element} the file input card item
 */
export const FileInputUploadItem = ({
  name,
  size,
  extension,
  onClick,
  onRemove,
  readOnly,
  fileKey,
  className = '',
  containerClassName = '',
  onDownload,
}: FileInputItemProps) => {
  const { getIcon } = useFileIcon();

  return (
    <div
      className={clsx(
        'relative group rounded-[5px] border border-solid border-non-hover-border hover:border-gray-300 hover:bg-gray-100 shadow-[0_0_24px_rgba(0,0,0,0.07)]',
        containerClassName,
      )}
      key={fileKey}
    >
      <div
        className={clsx(
          'flex items-start max-h-[62px] max-w-[176px] rounded bg-transparent box-border gap-3 py-2.5 pl-4 w-full pr-2',
          className,
        )}
      >
        <button
          type="button"
          onClick={onClick}
          className="flex items-center gap-3 border-0 bg-transparent p-0 hover:cursor-pointer flex-1 min-w-0"
        >
          {getIcon(extension)}
          <div className="overflow-hidden w-full">
            <Heading
              size="sm"
              className="text-black-heading text-ellipsis whitespace-nowrap overflow-hidden text-start leading-6 tracking-[-0.05px]"
            >
              {name}
            </Heading>
            <Body className="text-[12px] text-black-heading text-start">
              {getHumanReadableFileSize(size, true)}
            </Body>
          </div>
        </button>
        {!readOnly && (
          <IconButton
            className="rounded flex h-3 w-3 cursor-pointer items-center justify-center p-0 hover:bg-[#F1F3F8] shrink-0 mt-1"
            onClick={onRemove}
            icon="Close"
            label="Remove file"
            variant="minimal"
          />
        )}
        {readOnly && (
          <IconButton
            className="flex h-4 w-4 cursor-pointer items-center justify-center rounded-full p-0 hover:bg-[#F1F3F8] [&_svg]:h-4 [&_svg]:w-4 mt-1"
            onClick={onDownload}
            icon="Download"
            label="Download file"
            variant="minimal"
          />
        )}
      </div>
    </div>
  );
};
